import React, { useState } from 'react';
import { User, Mail, Phone, Users, Send, CheckCircle } from 'lucide-react';
import '../styles/globals.css';
import '../App.css';
import Konsensus_Constants from '../constants/index.js'
import axios from "axios";

const ContactUsForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    role: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const roleOptions = [
    { value: 'owner', label: 'Property Owner', icon: '🏠' },
    { value: 'tenant', label: 'Tenant', icon: '🔑' },
    { value: 'trustee', label: 'Trustee', icon: '⚖️' },
    { value: 'agent', label: 'Estate Agent', icon: '🏢' },
    { value: 'other', label: 'Other', icon: '👤' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({
      ...prev,
      role: value
    }));

    if (errors.role) {
      setErrors(prev => ({
        ...prev,
        role: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    } else if (!/^\+?[\d\s-()]+$/.test(formData.contactNumber)) {
      newErrors.contactNumber = 'Please enter a valid contact number';
    }

    if (!formData.role) {
      newErrors.role = 'Please select your role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async () => {
  if (validateForm()) {
    setIsSubmitting(true);

    try {
      const responses = await Promise.all([
        axios.post(Konsensus_Constants.API_URL + '/api/sendemail', {
          to: formData.email,
          subject: 'Thank you for contacting Konsensus',
          type: 'confirmation',
          firstName: formData.firstName,
          lastName: formData.lastName,
        }),
        axios.post(Konsensus_Constants.API_URL + '/api/sendemail', {
          to: 'deon@konsensus.tech',
          subject: `New Contact Form Submission - ${formData.firstName} ${formData.lastName}`,
          type: 'notification',
          firstName: formData.firstName,
          lastName: formData.lastName,
          userEmail: formData.email,
          contactNumber: formData.contactNumber,
          role: formData.role
        })
      ]);

      // ✅ Axios returns parsed data directly
      const results = responses.map(r => r.data);

      if (results.every(result => result.success)) {
        setIsSubmitting(false);
        setIsSubmitted(true);

        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            contactNumber: '',
            role: ''
          });
        }, 3000);
      } else {
        throw new Error('One or more emails failed to send');
      }

    } catch (error) {
      console.error('Email sending failed:', error);
      alert('Failed to send message. Please try again.');
      setIsSubmitting(false);
    }
  }
};


  if (isSubmitted) {
    return (
      <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
          <p className="text-gray-600">
            We've received your message and will get back to you within 24 hours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 transform transition-all duration-300 hover:shadow-3xl" style={{width:"100%"}}>
      <div className="text-center mb-8">
        <div
          className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4"
          style={{ backgroundColor: '#00aaaf' }}
        >
          <Mail className="h-6 w-6 text-white" />
        </div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
          Get In Touch
        </h2>
        <p className="text-gray-600">We'd love to hear from you. Send us a message!</p>
      </div>

      <div className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div className="group">
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2" style={{textAlign: "left"}}>
              First Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                }`}
                placeholder="John"
              />
            </div>
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600 animate-pulse">{errors.firstName}</p>
            )}
          </div>

          <div className="group">
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2" style={{textAlign: "left"}}>
              Last Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                }`}
                placeholder="Doe"
              />
            </div>
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600 animate-pulse">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email Field */}
        <div className="group">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" style={{textAlign: "left"}}>
            Email Address *
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              placeholder="john.doe@example.com"
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-600 animate-pulse">{errors.email}</p>
          )}
        </div>

        {/* Contact Number Field */}
        <div className="group">
          <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-2" style={{textAlign: "left"}}>
            Contact Number *
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="tel"
              id="contactNumber"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                errors.contactNumber ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              placeholder="+1 (555) 123-4567"
            />
          </div>
          {errors.contactNumber && (
            <p className="mt-1 text-sm text-red-600 animate-pulse">{errors.contactNumber}</p>
          )}
        </div>

        {/* Role Selection */}
        <div>
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-3" style={{textAlign: "left"}}>
              I am a: *
            </legend>
            <div className="grid grid-cols-1 gap-3">
              {roleOptions.map((option) => (
                <label
                  key={option.value}
                  className={`relative flex items-center p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
                    formData.role === option.value
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={option.value}
                    checked={formData.role === option.value}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-lg mr-3">{option.icon}</span>
                  <span className="text-sm font-medium text-gray-700 flex-1">
                    {option.label}
                  </span>
                  {formData.role === option.value && (
                    <CheckCircle className="h-5 w-5 text-blue-500" />
                  )}
                </label>
              ))}
            </div>
            {errors.role && (
              <p className="mt-2 text-sm text-red-600 animate-pulse">{errors.role}</p>
            )}
          </fieldset>
        </div>

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full flex items-center justify-center py-3 px-6 rounded-xl font-medium text-white transition-all duration-200 transform ${
            isSubmitting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
          }`}
          style={{
            backgroundColor: isSubmitting ? undefined : '#00aaaf',
            ...(isSubmitting ? {} : {
              ':hover': {
                backgroundColor: '#009398'
              }
            })
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.target.style.backgroundColor = '#009398';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.target.style.backgroundColor = '#00aaaf';
            }
          }}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Sending...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Send Message
            </>
          )}
        </button>
      </div>

      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500">
          We respect your privacy and will never share your information.
        </p>
      </div>
    </div>
  );
};

export default ContactUsForm;